import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    SxProps,
    Typography
} from '@mui/material'
import { FlexBox, FlexBoxColumn, FlexBoxSpaceBetween } from 'components/atoms'
import { useBetContext } from 'contexts/BetContext'
import { TranslationContext, useTranslation } from 'contexts/TranslationContext'
import dayjs from 'dayjs'
import { CrossIcon } from 'icons/CrossIcon'
import { BETS_CONFIG } from 'models'
import { useContext, useEffect, useMemo, useState } from 'react'
import {
    addToSessionStorage,
    formatBetAmount,
    getBetCount,
    getBetDesignation,
    getFromSessionStorage,
    parseBetDesignationArray,
    pluralize,
    REGISTER_AFFILIATE_LINK,
    removeDuplicates
} from 'utils'

export const BetValidator = ({
    betAmount,
    hideActions = false,
    sx = {}
}: {
    betAmount?: number
    hideActions?: boolean
    sx?: SxProps
}) => {
    const {
        bets,
        selectedBets,
        selectedBetType,
        race,
        selectedFormulaKey,
        availableRunners,
        focusedInputId,
        handleTrackingEvent,
        handleAffiliateLink,
        handlePmuSubmit,
        betError,
        selectedOperator,
        mainColor
    } = useBetContext()

    const [
        promptForExistingAccountVisible,
        setPromptForExistingAccountVisible
    ] = useState(false)

    const [dontShowPromptAgain, setDontShowPromptAgain] = useState(
        getFromSessionStorage('dontShowPMUPromptAgain') === 'true'
    )

    const { language } = useContext(TranslationContext)
    const translation = useTranslation()

    const formattedDate = dayjs(race?.stDt).format('HH:mm').replace(':', 'h')

    useEffect(() => {
        if (dontShowPromptAgain)
            addToSessionStorage('dontShowPMUPromptAgain', 'true')
    }, [dontShowPromptAgain])

    const { base, associates } = useMemo(
        () =>
            parseBetDesignationArray(
                Array.from(selectedBets.values() || []),
                selectedBetType
            ),
        [bets, selectedBetType, selectedFormulaKey]
    )

    const betCount = useMemo(
        () =>
            getBetCount({
                bets,
                selectedBets,
                selectedBetType,
                selectedFormulaKey,
                availableRunners
            }),
        [bets, selectedBetType, selectedFormulaKey]
    )

    const betSum = useMemo(() => {
        const currentBets = Array.from(selectedBets.values() || [])
        const availableBets = currentBets.filter(
            (bet) => bet.stake && bet.stake > 0
        )
        const totalStake = availableBets.reduce(
            (acc, bet) => acc + bet.stake!,
            0
        )

        if (totalStake === 0) return 0
        if (selectedBetType !== 'S') {
            return formatBetAmount(
                (betCount / availableBets.length) * totalStake
            )
        }
        return formatBetAmount(totalStake)
    }, [bets, selectedBetType, selectedFormulaKey])

    const optionalBetTypes = removeDuplicates(
        Array.from(selectedBets.values() || [])
            .filter((bet) => bet.stake && bet.stake > 0)
            .map((bet) => BETS_CONFIG[bet.betType]?.types?.[bet.formulaRisk!])
    ) as string[]

    const betTypesWording = useMemo(() => {
        const betLabel =
            BETS_CONFIG[selectedBetType]?.operatorLabel?.[selectedOperator] ||
            BETS_CONFIG[selectedBetType]?.label ||
            ''
        if (focusedInputId) {
            const bet = selectedBets.get(focusedInputId)
            if (!bet) return betLabel
            return `${betLabel} ${BETS_CONFIG[bet.betType]?.types?.[bet.formulaRisk!] || ''}`
        }

        return `${betLabel} ${optionalBetTypes.join(' / ')}`
    }, [bets, selectedBetType, focusedInputId])

    const betCountWords = useMemo(() => {
        if (language === 'fr')
            return betCount > 0
                ? `${betCount} ${pluralize(translation.bet.toLowerCase(), betCount)} ${pluralize(translation.unit.toLowerCase(), betCount)}`
                : translation.invalidBet
        else
            return betCount > 0
                ? `${betCount} ${translation.unit.toLowerCase()} ${pluralize(translation.bet.toLowerCase(), betCount)}`
                : translation.invalidBet
    }, [betCount])

    const designation = useMemo(() => {
        if (focusedInputId)
            return selectedBets.get(focusedInputId!)?.designation
        if (selectedBetType === 'S') return base.join('-')
        const betDesignation = getBetDesignation({
            base,
            associates,
            formulaKey: selectedFormulaKey,
            betType: selectedBetType
        })

        if (selectedFormulaKey === 'UNIT')
            return betDesignation.replaceAll('X', '?')
        return betDesignation
    }, [bets, selectedBetType, selectedFormulaKey, focusedInputId])

    const betsAreAllIdentical = useMemo(() => {
        const availableBets = Array.from(selectedBets.values())
        if (availableBets.length === 0) return true

        const winBets = availableBets.filter((bet) => bet.formulaRisk === 'WIN')
        const placeBets = availableBets.filter(
            (bet) => bet.formulaRisk === 'PLACE'
        )

        const hasWinStake = winBets.some((bet) => bet.stake && bet.stake > 0)
        const hasPlaceStake = placeBets.some(
            (bet) => bet.stake && bet.stake > 0
        )

        const comparisonStake = availableBets.find(
            (bet) => bet.stake && bet.stake > 0
        )?.stake

        if (hasWinStake) {
            const allWinBetsHaveSameStake = winBets.every(
                (bet) => bet.stake && bet.stake === comparisonStake
            )
            if (!allWinBetsHaveSameStake) return false
        }

        if (hasPlaceStake) {
            const allPlaceBetsHaveSameStake = placeBets.every(
                (bet) => bet.stake && bet.stake === comparisonStake
            )
            if (!allPlaceBetsHaveSameStake) return false
        }

        return true
    }, [selectedBets, selectedBets.size, focusedInputId])

    const onPmuSubmit = () => {
        void handleTrackingEvent({
            eventType: 'SUBMIT',
            actionType: betSum.toString()
        })

        const availableBets = Array.from(selectedBets.values()).filter(
            (bet) => bet.stake && bet.stake > 0
        )
        if (!availableBets.length) return

        handlePmuSubmit({
            designation: designation || '',
            betAmount: availableBets[0].stake?.toString() || '0',
            optionalBetTypes: optionalBetTypes || []
        })
    }

    const onPmuSignUp = () => {
        void handleTrackingEvent({
            eventType: 'SUBMIT',
            actionType: 'SIGN_UP'
        })
        window.open(REGISTER_AFFILIATE_LINK)
        setPromptForExistingAccountVisible(false)
    }

    const onBannerClose = () => {
        setDontShowPromptAgain(true)
        return setPromptForExistingAccountVisible(false)
    }

    if (!selectedBetType) return null
    return (
        <>
            <FlexBoxColumn
                sx={{
                    backgroundColor: '#F4F6FA',
                    ...sx
                }}
            >
                {!dontShowPromptAgain &&
                    selectedOperator === 'PMU' &&
                    !hideActions && (
                        <FlexBoxColumn
                            sx={{
                                width: '100%',
                                justifyContent: 'center',
                                backgroundColor: mainColor,
                                color: 'white',
                                position: 'relative',
                                p: 1
                            }}
                        >
                            <Typography variant="text-12-regular">
                                {translation.pmuShortAccountRequired}
                            </Typography>
                            <FlexBoxColumn sx={{ mt: 1, gap: 1 }}>
                                <Button
                                    variant="contained"
                                    onClick={onPmuSignUp}
                                    sx={{
                                        width: '100%'
                                    }}
                                >
                                    <Typography variant="text-12-regular">
                                        {
                                            translation.createShortAccountAndGetBonus
                                        }
                                    </Typography>
                                </Button>
                                <Button
                                    onClick={onBannerClose}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0
                                    }}
                                >
                                    <CrossIcon size={16} color="#fff" />
                                </Button>
                            </FlexBoxColumn>
                        </FlexBoxColumn>
                    )}

                <FlexBoxColumn
                    sx={{
                        p: 2,
                        width: 'fill-available'
                    }}
                >
                    <FlexBoxSpaceBetween
                        sx={{ width: '100%', alignItems: 'flex-start' }}
                    >
                        {race && (
                            <Box sx={{ width: '100%' }}>
                                <Typography
                                    variant="text-13-regular"
                                    component={'p'}
                                    sx={{ mb: 1 }}
                                >
                                    {`R${race?.cNumber}C${race?.nb}`} -{' '}
                                    {formattedDate}
                                </Typography>
                                <Typography
                                    variant="title-14-bold"
                                    component={'p'}
                                >
                                    {betTypesWording}
                                    <br />
                                    {designation}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ width: '100%', textAlign: 'right' }}>
                            {focusedInputId ? (
                                <>
                                    <Typography
                                        variant="text-13-regular"
                                        component={'p'}
                                        sx={{ mb: 1 }}
                                    >
                                        {translation.selectedBet} :
                                    </Typography>
                                    <Typography
                                        variant="title-16-bold"
                                        component={'p'}
                                        sx={{
                                            fontSize: 32,
                                            lineHeight: '110%',
                                            color: mainColor
                                        }}
                                    >
                                        {betAmount !== undefined
                                            ? betAmount
                                            : betSum}{' '}
                                        €
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography
                                        variant="title-14-bold"
                                        component={'p'}
                                    >
                                        {translation.totalBet} :
                                    </Typography>
                                    <Typography
                                        variant="title-16-bold"
                                        component={'p'}
                                        sx={{ fontSize: 20, mb: 0.5 }}
                                    >
                                        {betSum} €
                                    </Typography>
                                    <Typography
                                        variant="text-12-regular"
                                        component={'p'}
                                    >
                                        {betCountWords}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </FlexBoxSpaceBetween>
                    {!hideActions && (
                        <>
                            {selectedBets.size > 0 && (
                                <Button
                                    variant="contained"
                                    sx={{
                                        width: '80%',
                                        mt: 2.5,
                                        fontWeight: 'bold',
                                        textTransform: 'uppercase',
                                        backgroundColor: mainColor
                                    }}
                                    disabled={
                                        betError ||
                                        betSum === 0 ||
                                        betCount === 0 ||
                                        (selectedOperator === 'PMU' &&
                                            !betsAreAllIdentical)
                                    }
                                    onClick={() => {
                                        if (selectedOperator === 'PMU') {
                                            if (dontShowPromptAgain) {
                                                return onPmuSubmit()
                                            }
                                            return setPromptForExistingAccountVisible(
                                                true
                                            )
                                        }

                                        handleAffiliateLink()
                                    }}
                                >
                                    {selectedOperator === 'PMU'
                                        ? translation.playBySms
                                        : `${translation.validateOn} ${selectedOperator?.toUpperCase() || ''}`}
                                </Button>
                            )}
                            <FlexBox sx={{ mt: 2, flexDirection: 'column' }}>
                                {selectedOperator === 'GENYBET' && (
                                    <>
                                        <Typography
                                            variant="text-12-regular"
                                            component="p"
                                        >
                                            {translation.notRegisteredYet}
                                        </Typography>
                                        <Button
                                            variant="text"
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 'bold',
                                                p: 0
                                            }}
                                            onClick={() => {
                                                void handleTrackingEvent({
                                                    eventType: 'SUBMIT',
                                                    actionType: 'SIGN_UP'
                                                })
                                                handleAffiliateLink(true)
                                            }}
                                        >
                                            {translation.signUp}
                                        </Button>
                                    </>
                                )}
                                {selectedOperator === 'PMU' &&
                                !betsAreAllIdentical &&
                                selectedBets.size > 0 ? (
                                    <Box
                                        sx={{
                                            p: 1,
                                            borderRadius: 1,
                                            backgroundColor: '#FFCCCB'
                                        }}
                                    >
                                        <Typography
                                            variant="text-12-regular"
                                            component="p"
                                        >
                                            {translation.smsBetConditions}
                                        </Typography>
                                    </Box>
                                ) : null}
                            </FlexBox>
                        </>
                    )}
                </FlexBoxColumn>
            </FlexBoxColumn>

            <Dialog
                open={promptForExistingAccountVisible}
                onClose={() => setPromptForExistingAccountVisible(false)}
            >
                <DialogTitle>{translation.warning}</DialogTitle>
                <DialogContent sx={{ px: 3 }}>
                    <DialogContentText>
                        {translation.pmuAccountRequired}
                    </DialogContentText>

                    <FlexBox
                        sx={{
                            mt: 4,
                            gap: 1,
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={() => {
                                onPmuSubmit()
                                setPromptForExistingAccountVisible(false)
                            }}
                            sx={{
                                fontSize: {
                                    xs: '0.7rem',
                                    sm: '0.875rem'
                                },
                                width: {
                                    xs: '100%'
                                },
                                border: `1px solid ${mainColor} !important`,
                                color: mainColor
                            }}
                        >
                            {translation.pmuAccountCreated}
                        </Button>
                        <Button
                            onClick={onPmuSignUp}
                            autoFocus
                            variant="contained"
                            sx={{
                                fontSize: {
                                    xs: '0.7rem',
                                    sm: '0.875rem'
                                },
                                width: {
                                    xs: '100%'
                                },
                                backgroundColor: mainColor,
                                whiteSpace: 'pre-line'
                            }}
                        >
                            {translation.createAccountAndGetBonus.replace(
                                '(',
                                '\n('
                            )}
                        </Button>
                    </FlexBox>

                    <FlexBox sx={{ width: '100%', justifyContent: 'center' }}>
                        <Checkbox
                            sx={{ color: `${mainColor} !important` }}
                            value={dontShowPromptAgain}
                            onChange={(e) =>
                                setDontShowPromptAgain(e.target.checked)
                            }
                        />
                        <Typography variant="text-12-regular">
                            {translation.dontAskAgain}
                        </Typography>
                    </FlexBox>
                </DialogContent>
            </Dialog>
        </>
    )
}
